import { AnsweringClient } from "../clients/answeringClient";
import { AuthRetry, SessionExpiredError } from "../utils/authRetry";

export interface IAssignmentMetadata {
  type: string;
  backEnabled: boolean;
  consentTermId: number;
  description: string;
  instructions: string;
  lastAnsweredQuestion: number;
  title: string;
  totalQuestions: number;
  allow_audio: boolean;
}

export interface InstructionsControllerDelegate {
  didGetAssignmentToken(token: string): void;
  didGetAssignmentMetadata(metadata: IAssignmentMetadata): void;
  applicationShouldLogout(): void;
  genericError(message?: string): void;
}

export class InstructionsController {
  private readonly auth: AuthRetry;

  constructor(
    private delegate: InstructionsControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {
    this.auth = new AuthRetry(this.answeringClient, () =>
      this.delegate.applicationShouldLogout()
    );
  }

  public async getAssignmentToken(accessToken: string, instrumentId: string) {
    try {
      const response = await this.auth.run(accessToken, (token) =>
        this.answeringClient.getInstrumentToken(token, instrumentId)
      );
      this.delegate.didGetAssignmentToken(response.data.token);
    } catch (error: any) {
      this.reportError(error);
    }
  }

  public async getAssignmentMetadata(
    accessToken: string,
    assignmentToken: string
  ) {
    try {
      const response = await this.auth.run(accessToken, (token) =>
        this.answeringClient.getInstrumentMetadata(token, assignmentToken)
      );
      this.delegate.didGetAssignmentMetadata(response.data);
    } catch (error: any) {
      this.reportError(error);
    }
  }

  /** Swallows SessionExpiredError rather than reporting it; see its declaration. */
  private reportError(error: any): void {
    if (error instanceof SessionExpiredError) {
      return;
    }
    this.delegate.genericError(this.extractErrorMessage(error));
  }

  private extractErrorMessage(error: any): string | undefined {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message
    );
  }
}
